import { API_URLS } from '../config'; // Import API_URLS

/**
 * Validates the token stored in localStorage against the backend.
 * @returns {Promise<boolean>} True if the token is valid, false otherwise.
 */
const validateToken = async () => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    try {
        // Use the verification endpoint from config
        const response = await fetch(API_URLS.verifyToken, { // Use config
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            // If validation fails (e.g., expired, invalid), log out
            logout();
            return false;
        }

        const data = await response.json();
        // Assuming the backend returns { isValid: true } or similar
        if (!data.isValid) {
             logout();
             return false;
        }
        return true; // Token is valid
    } catch (error) {
        console.error('Token validation error:', error);
        logout(); // Log out on network or other errors
        return false;
    }
};

/**
 * Attempts to log in the user with the provided credentials.
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @returns {Promise<{success: boolean, user?: object, message?: string}>} Result of the login attempt.
 */
const login = async (email, password) => {
    try {
        const response = await fetch(API_URLS.login, { // Use config
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });
        const data = await response.json();

        if (response.ok) {
            console.log('Login successful:', data);
            localStorage.setItem('token', data.token);
            const user = data.user; // Parse user object from response
            console.log('User data:', user);
            localStorage.setItem('businessId', user.businessId || ''); // Store businessId if available
            localStorage.setItem('businessName', user.businessName || ''); // Store businessName if available
            localStorage.setItem('name', user.userName || ''); // Store userName if available
            localStorage.setItem('email', user.email || ''); // Store user object
            // Optionally dispatch a custom event to notify other parts of the app
            window.dispatchEvent(new Event('authChange'));
            return { success: true, user: user };
        } else {
            return { success: false, message: data.message || "Invalid credentials" };
        }
    } catch (err) {
        console.error('Login error:', err);
        return { success: false, message: "An error occurred during login." };
    }
};

/**
 * Logs the user out by removing token and user data from localStorage.
 */
const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Optionally dispatch a custom event to notify other parts of the app
    window.dispatchEvent(new Event('authChange'));
};

/**
 * Checks if a token exists in localStorage (basic check).
 * For a definitive check, use validateToken.
 * @returns {boolean} True if a token exists, false otherwise.
 */
const isAuthenticated = () => {
    return localStorage.getItem("token") !== null;
};

/**
 * Retrieves the user object from localStorage.
 * @returns {object | null} The parsed user object or null if not found.
 */
const getUser = () => {
    const user = localStorage.getItem('user');
    try {
        return user ? JSON.parse(user) : null;
    } catch (e) {
        console.error("Error parsing user data from localStorage", e);
        return null;
    }
};

export const AuthService = {
    validateToken,
    login,
    logout,
    isAuthenticated,
    getUser,
};
